import { motion } from 'framer-motion'
import {
    AlertTriangle,
    ArrowDownRight,
    ArrowLeft,
    ArrowRight,
    ArrowUpRight,
    BadgeIndianRupee,
    Banknote,
    BellRing,
    Building2,
    CheckCircle2,
    CircleSlash,
    Clock3,
    Coins,
    FlaskConical,
    FolderKanban,
    Info,
    IndianRupee,
    Landmark,
    LoaderCircle,
    MoreHorizontal,
    Package,
    PieChart,
    Receipt,
    RefreshCw,
    ShieldAlert,
    Sparkles,
    Tag,
    TrendingDown,
    TrendingUp,
    Users,
    Wallet,
    Zap,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './ExpensesSummary.module.css'

const parseActiveStore = (activeStore) => {
    try {
        return (activeStore && activeStore.trim() !== '') ? JSON.parse(activeStore) : null
    } catch {
        return null
    }
}

const toMonthKey = (date = new Date()) => (
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
)

const getPrevMonth = (monthKey) => {
    const [year, month] = String(monthKey || '').split('-').map(Number)
    if (!year || !month) return toMonthKey()
    const d = new Date(year, month - 1, 1)
    d.setMonth(d.getMonth() - 1)
    return toMonthKey(d)
}

const getNextMonth = (monthKey) => {
    const [year, month] = String(monthKey || '').split('-').map(Number)
    if (!year || !month) return toMonthKey()
    const d = new Date(year, month - 1, 1)
    d.setMonth(d.getMonth() + 1)
    return toMonthKey(d)
}

const isValidFiscalPeriod = (value) => /^\d{4}-(0[1-9]|1[0-2])$/.test(String(value || ''))

const formatMonthLabel = (monthKey) => {
    const [year, month] = String(monthKey || '').split('-').map(Number)
    if (!year || !month) return '—'
    const d = new Date(year, month - 1, 1)
    return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
}

const fmtCurrency = (value) => Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
})

const fmtAmountShort = (value) => {
    const numeric = Number(value || 0)
    if (Math.abs(numeric) >= 10000000) return `₹${(numeric / 10000000).toFixed(2)}Cr`
    if (Math.abs(numeric) >= 100000) return `₹${(numeric / 100000).toFixed(2)}L`
    if (Math.abs(numeric) >= 1000) return `₹${(numeric / 1000).toFixed(1)}K`
    return `₹${fmtCurrency(numeric)}`
}

const formatExpenseDate = (value) => {
    if (!value) return '—'
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return value
    return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const ICON_MAP = {
    FlaskConical,
    Users,
    Building2,
    Zap,
    MoreHorizontal,
    Tag,
    Receipt,
    Wallet,
    Package,
    Landmark,
}

const getCategoryIcon = (iconSlug) => ICON_MAP[iconSlug] || Tag

const getTrendMeta = (comparison) => {
    const trend = comparison?.trend || 'flat'
    if (trend === 'up') {
        return {
            icon: ArrowUpRight,
            className: styles.trendUp,
            text: `+${Math.abs(Number(comparison?.changePercent || 0)).toFixed(1)}% · +₹${fmtCurrency(Math.abs(comparison?.changeAmount || 0))} vs last month`,
        }
    }
    if (trend === 'down') {
        return {
            icon: ArrowDownRight,
            className: styles.trendDown,
            text: `${Math.abs(Number(comparison?.changePercent || 0)).toFixed(1)}% less · ₹${fmtCurrency(Math.abs(comparison?.changeAmount || 0))} saved`,
        }
    }
    if (trend === 'new') {
        return {
            icon: Sparkles,
            className: styles.trendNeutral,
            text: 'First tracked month for this store',
        }
    }
    return {
        icon: Coins,
        className: styles.trendNeutral,
        text: 'No change vs last month',
    }
}

const ATTENTION_META = {
    warning: { icon: AlertTriangle, className: styles.attentionWarning },
    error: { icon: ShieldAlert, className: styles.attentionError },
    info: { icon: Info, className: styles.attentionInfo },
}

const getAttentionRoute = (type, fiscalPeriod) => {
    if (type === 'pending_approval') return '/expenses/review'
    if (type === 'itc_unclaimed') return `/expenses?isGstItcClaimed=0&fiscalPeriod=${encodeURIComponent(fiscalPeriod || '')}`
    if (type === 'rejected') return '/expenses?approvalStatus=rejected'
    if (type === 'unpaid') return '/expenses?paymentStatus=unpaid'
    if (type === 'partial') return '/expenses?paymentStatus=partial'
    return '/expenses'
}

const getDefaultDashboard = ({ storeIdentifier, fiscalPeriod }) => ({
    storeIdentifier,
    fiscalPeriod,
    generatedAt: '',
    currentPeriod: {
        totalExpense: 0,
        subTotal: 0,
        totalGst: 0,
        totalBills: 0,
        fiscalPeriod,
    },
    comparison: {
        lastPeriod: getPrevMonth(fiscalPeriod),
        lastPeriodExpense: 0,
        changeAmount: 0,
        changePercent: 0,
        trend: 'flat',
    },
    outstanding: {
        totalBalanceDue: 0,
        unpaidBillCount: 0,
        partialBillCount: 0,
        totalBillCount: 0,
    },
    attention: [],
    categoryBreakdown: [],
    expenseTypeSplit: {
        opexTotal: 0,
        capexTotal: 0,
        mixedTotal: 0,
        opexPercent: 0,
        capexPercent: 0,
        mixedPercent: 0,
    },
    gstSummary: {
        totalGstPaid: 0,
        itcEligibleAmount: 0,
        itcClaimedAmount: 0,
        itcClaimedBillCount: 0,
        itcUnclaimedAmount: 0,
        itcUnclaimedBillCount: 0,
    },
    recentExpenses: [],
})

const normalizeCategory = (category) => {
    const storeCategoryIdentifier = category?.storeCategoryIdentifier || ''
    const isOthers = storeCategoryIdentifier === 'others'
    return {
        ...category,
        storeCategoryIdentifier,
        categoryLabel: category?.categoryLabel || (isOthers ? 'Others' : 'Uncategorized'),
        colorHex: category?.colorHex || (isOthers ? '#B4B2A9' : '#888780'),
        iconSlug: category?.iconSlug || (isOthers ? 'MoreHorizontal' : 'Tag'),
    }
}

const normalizeDashboard = ({ response, storeIdentifier, fiscalPeriod }) => {
    const fallback = getDefaultDashboard({ storeIdentifier, fiscalPeriod })
    const responseFiscalPeriod = isValidFiscalPeriod(response?.fiscalPeriod) ? response.fiscalPeriod : fiscalPeriod

    return {
        ...fallback,
        storeIdentifier: response?.storeIdentifier || storeIdentifier,
        fiscalPeriod: responseFiscalPeriod,
        generatedAt: response?.generatedAt || '',
        currentPeriod: {
            ...fallback.currentPeriod,
            ...(response?.currentPeriod || {}),
            fiscalPeriod: response?.currentPeriod?.fiscalPeriod || responseFiscalPeriod,
        },
        comparison: {
            ...fallback.comparison,
            ...(response?.comparison || {}),
            lastPeriod: response?.comparison?.lastPeriod || getPrevMonth(responseFiscalPeriod),
        },
        outstanding: {
            ...fallback.outstanding,
            ...(response?.outstanding || {}),
        },
        attention: Array.isArray(response?.attention) ? response.attention : [],
        categoryBreakdown: Array.isArray(response?.categoryBreakdown)
            ? response.categoryBreakdown.map(normalizeCategory)
            : [],
        expenseTypeSplit: {
            ...fallback.expenseTypeSplit,
            ...(response?.expenseTypeSplit || {}),
        },
        gstSummary: {
            ...fallback.gstSummary,
            ...(response?.gstSummary || {}),
        },
        recentExpenses: Array.isArray(response?.recentExpenses) ? response.recentExpenses : [],
    }
}

const ExpensesSummary = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const navigate = useNavigate()

    const activeStore = parseActiveStore(asCtx?.activeStore)
    const storeIdentifier = activeStore?.storeIdentifier || ''

    const [fiscalPeriod, setFiscalPeriod] = useState(toMonthKey())
    const [dashboard, setDashboard] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    const loadDashboard = useCallback(async (targetFiscalPeriod = fiscalPeriod, { silent = false } = {}) => {
        if (!storeIdentifier) {
            setDashboard(null)
            setErrorMessage('No store selected.')
            return false
        }

        if (!isValidFiscalPeriod(targetFiscalPeriod)) {
            setDashboard(null)
            setErrorMessage('Invalid month selected. Use YYYY-MM format.')
            return false
        }

        if (silent) {
            setIsRefreshing(true)
        } else {
            setIsLoading(true)
        }
        setErrorMessage('')

        try {
            const payload = {
                storeIdentifier,
            }

            if (authCtx?.token) {
                payload.token = authCtx.token
            }

            if (targetFiscalPeriod && targetFiscalPeriod !== toMonthKey()) {
                payload.fiscalPeriod = targetFiscalPeriod
            }

            const response = await fetch(BASE_URL_API + 'fetchExpensesSummary', {
                method: 'POST',
                body: JSON.stringify(payload),
            })

            const result = await response.json()

            if (result?.message === 'tokenExpired') {
                authCtx.logout()
                return false
            }

            if (result?.status === 'success') {
                setDashboard(normalizeDashboard({
                    response: result?.response,
                    storeIdentifier,
                    fiscalPeriod: targetFiscalPeriod,
                }))
                return true
            }

            setDashboard(null)
            setErrorMessage(result?.message || 'Unable to load expense summary.')
            return false
        } catch {
            setDashboard(null)
            setErrorMessage('Unable to connect to server.')
            return false
        } finally {
            setIsLoading(false)
            setIsRefreshing(false)
        }
    }, [authCtx, fiscalPeriod, storeIdentifier])

    useEffect(() => {
        loadDashboard(fiscalPeriod)
    }, [loadDashboard, fiscalPeriod])

    useEffect(() => {
        const onExpenseMutation = () => {
            loadDashboard(fiscalPeriod, { silent: true })
        }

        window.addEventListener('expenses-dashboard:refresh', onExpenseMutation)
        return () => {
            window.removeEventListener('expenses-dashboard:refresh', onExpenseMutation)
        }
    }, [loadDashboard, fiscalPeriod])

    const currentMonth = toMonthKey()
    const canGoNextMonth = fiscalPeriod < currentMonth

    const trendMeta = useMemo(() => getTrendMeta(dashboard?.comparison || {}), [dashboard?.comparison])

    const donutGradient = useMemo(() => {
        const split = dashboard?.expenseTypeSplit || {}
        const opex = Math.max(0, Number(split?.opexPercent || 0))
        const capex = Math.max(0, Number(split?.capexPercent || 0))
        const mixed = Math.max(0, Number(split?.mixedPercent || 0))
        const total = opex + capex + mixed
        if (total <= 0) {
            return 'conic-gradient(#dbeafe 0deg 360deg)'
        }

        const opexDeg = (opex / total) * 360
        const capexDeg = (capex / total) * 360
        const mixedDeg = 360 - opexDeg - capexDeg

        return `conic-gradient(
            #10b981 0deg ${opexDeg}deg,
            #3b82f6 ${opexDeg}deg ${opexDeg + capexDeg}deg,
            #a855f7 ${opexDeg + capexDeg}deg ${opexDeg + capexDeg + mixedDeg}deg
        )`
    }, [dashboard?.expenseTypeSplit])

    const onChangeMonth = (nextMonth) => {
        setFiscalPeriod(nextMonth)
    }

    const onCategoryTap = (category) => {
        if (category?.storeCategoryIdentifier === 'others') {
            navigate(`/expenses?fiscalPeriod=${encodeURIComponent(fiscalPeriod)}`)
            return
        }
        navigate(`/expenses?fiscalPeriod=${encodeURIComponent(fiscalPeriod)}&storeCategoryIdentifier=${encodeURIComponent(category?.storeCategoryIdentifier || '')}`)
    }

    const onRefresh = async () => {
        const isSuccess = await loadDashboard(fiscalPeriod, { silent: true })
        if (isSuccess) {
            toast.success('Expense summary refreshed.', {
                position: 'top-center',
                hideProgressBar: true,
                autoClose: 1500,
            })
            return
        }

        toast.error('Unable to refresh summary right now.', {
            position: 'top-center',
            hideProgressBar: true,
            autoClose: 1800,
        })
    }

    return (
        <div className={styles.page}>
            <motion.section
                className={styles.hero}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.28 }}
            >
                <div className={styles.heroHeadRow}>
                    <h1>Expenses Summary</h1>

                    <div className={styles.heroActions}>
                        <div className={styles.monthPicker}>
                            <button type="button" onClick={() => onChangeMonth(getPrevMonth(fiscalPeriod))}>
                                <ArrowLeft size={14} />
                            </button>
                            <input
                                type="month"
                                value={fiscalPeriod}
                                max={currentMonth}
                                onChange={(e) => onChangeMonth(e.target.value || currentMonth)}
                            />
                            <span>{formatMonthLabel(fiscalPeriod)}</span>
                            <button type="button" disabled={!canGoNextMonth} onClick={() => onChangeMonth(getNextMonth(fiscalPeriod))}>
                                <ArrowRight size={14} />
                            </button>
                        </div>

                        <button type="button" className={styles.refreshBtn} onClick={onRefresh} disabled={isLoading || isRefreshing || !storeIdentifier}>
                            {isRefreshing ? <LoaderCircle size={14} className={styles.spin} /> : <RefreshCw size={14} />}
                            Refresh
                        </button>
                    </div>
                </div>
            </motion.section>

            {isLoading && (
                <section className={styles.loaderCard}>
                    <LoaderCircle size={18} className={styles.spin} />
                    <div>
                        <h3>Loading expense dashboard...</h3>
                        <p>Fetching complete summary snapshot for {formatMonthLabel(fiscalPeriod)}.</p>
                    </div>
                </section>
            )}

            {!isLoading && errorMessage && (
                <section className={styles.errorCard}>
                    <CircleSlash size={18} />
                    <div>
                        <h3>Unable to load summary</h3>
                        <p>{errorMessage}</p>
                    </div>
                    <button type="button" onClick={() => loadDashboard(fiscalPeriod)}>Retry</button>
                </section>
            )}

            {!isLoading && !errorMessage && dashboard && (
                <>
                    <section className={styles.kpiGrid}>
                        <article className={styles.kpiCard}>
                            <div className={styles.kpiHead}><IndianRupee size={14} /> Total Expense</div>
                            <h3>₹{fmtCurrency(dashboard.currentPeriod?.totalExpense)}</h3>
                            <p>{dashboard.currentPeriod?.totalBills || 0} approved bills in {formatMonthLabel(fiscalPeriod)}</p>
                        </article>

                        <article className={styles.kpiCard}>
                            <div className={styles.kpiHead}><FolderKanban size={14} /> Subtotal</div>
                            <h3>₹{fmtCurrency(dashboard.currentPeriod?.subTotal)}</h3>
                            <p>Pre-GST spend confirmation for this period</p>
                        </article>

                        <article className={styles.kpiCard}>
                            <div className={styles.kpiHead}><BadgeIndianRupee size={14} /> Total GST</div>
                            <h3>₹{fmtCurrency(dashboard.currentPeriod?.totalGst)}</h3>
                            <p>GST included in approved monthly spend</p>
                        </article>

                        <article className={styles.kpiCard}>
                            <div className={styles.kpiHead}><Banknote size={14} /> Outstanding</div>
                            <h3>₹{fmtCurrency(dashboard.outstanding?.totalBalanceDue)}</h3>
                            <p>{dashboard.outstanding?.totalBillCount || 0} unpaid/partial bills (all-time)</p>
                        </article>
                    </section>

                    <section className={styles.contentGrid}>
                        <article className={styles.panel}>
                            <div className={styles.panelHead}>
                                <h3><TrendingUp size={15} /> Trend vs Last Period</h3>
                                <span>{dashboard.comparison?.lastPeriod || '—'}</span>
                            </div>

                            <div className={`${styles.trendBadge} ${trendMeta.className}`}>
                                <trendMeta.icon size={14} />
                                <span>{trendMeta.text}</span>
                            </div>

                            <div className={styles.trendStats}>
                                <div>
                                    <span>Last Period Spend</span>
                                    <strong>₹{fmtCurrency(dashboard.comparison?.lastPeriodExpense)}</strong>
                                </div>
                                <div>
                                    <span>Change Amount</span>
                                    <strong>
                                        {Number(dashboard.comparison?.changeAmount || 0) < 0 ? '-' : '+'}
                                        ₹{fmtCurrency(Math.abs(dashboard.comparison?.changeAmount || 0))}
                                    </strong>
                                </div>
                            </div>
                        </article>

                        {Array.isArray(dashboard.attention) && dashboard.attention.length > 0 && (
                            <article className={`${styles.panel} ${styles.attentionPanel}`}>
                                <div className={styles.panelHead}>
                                    <h3><BellRing size={15} /> Attention Needed</h3>
                                    <span>{dashboard.attention.length} active alerts</span>
                                </div>

                                <div className={styles.attentionList}>
                                    {dashboard.attention.map((item) => {
                                        const meta = ATTENTION_META[item?.severity] || ATTENTION_META.info
                                        const Icon = meta.icon
                                        return (
                                            <button
                                                type="button"
                                                key={item?.type}
                                                className={`${styles.attentionItem} ${meta.className}`}
                                                onClick={() => navigate(getAttentionRoute(item?.type, fiscalPeriod))}
                                            >
                                                <Icon size={15} />
                                                <span>{item?.label || 'Needs attention'}</span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </article>
                        )}

                        <article className={styles.panel}>
                            <div className={styles.panelHead}>
                                <h3><PieChart size={15} /> Expense Type Split</h3>
                                <span>Approved · {formatMonthLabel(fiscalPeriod)}</span>
                            </div>

                            <div className={styles.splitWrap}>
                                <div className={styles.donut} style={{ background: donutGradient }}>
                                    <div className={styles.donutCenter}>₹{fmtAmountShort(dashboard.currentPeriod?.totalExpense).replace('₹', '')}</div>
                                </div>

                                <div className={styles.splitLegend}>
                                    <div><span className={styles.opexDot} /> OpEx {Number(dashboard.expenseTypeSplit?.opexPercent || 0).toFixed(1)}%</div>
                                    <div><span className={styles.capexDot} /> CapEx {Number(dashboard.expenseTypeSplit?.capexPercent || 0).toFixed(1)}%</div>
                                    <div><span className={styles.mixedDot} /> Mixed {Number(dashboard.expenseTypeSplit?.mixedPercent || 0).toFixed(1)}%</div>
                                </div>
                            </div>

                            <div className={styles.splitValues}>
                                <span>OpEx ₹{fmtCurrency(dashboard.expenseTypeSplit?.opexTotal)}</span>
                                <span>CapEx ₹{fmtCurrency(dashboard.expenseTypeSplit?.capexTotal)}</span>
                                <span>Mixed ₹{fmtCurrency(dashboard.expenseTypeSplit?.mixedTotal)}</span>
                            </div>
                        </article>

                        <article className={styles.panelWide}>
                            <div className={styles.panelHead}>
                                <h3><Receipt size={15} /> Category Breakdown</h3>
                                <span>Top 5 + Others</span>
                            </div>

                            {dashboard.categoryBreakdown.length === 0 ? (
                                <div className={styles.emptyState}>
                                    <CircleSlash size={16} /> No expenses recorded this month.
                                </div>
                            ) : (
                                <div className={styles.categoryRows}>
                                    {dashboard.categoryBreakdown.map((category) => {
                                        const Icon = getCategoryIcon(category?.iconSlug)
                                        const width = Math.max(4, Math.min(100, Number(category?.percentage || 0)))
                                        const color = category?.colorHex || '#888780'
                                        return (
                                            <button
                                                type="button"
                                                key={category?.storeCategoryIdentifier || category?.categoryLabel}
                                                className={styles.categoryRow}
                                                onClick={() => onCategoryTap(category)}
                                            >
                                                <div className={styles.categoryLabelWrap}>
                                                    <span className={styles.categoryIcon} style={{ color }}><Icon size={14} /></span>
                                                    <div>
                                                        <strong>{category?.categoryLabel || 'Uncategorized'}</strong>
                                                        <span>{category?.billCount || 0} bills</span>
                                                    </div>
                                                </div>

                                                <div className={styles.categoryBarWrap}>
                                                    <div className={styles.categoryBarTrack}>
                                                        <span className={styles.categoryBarFill} style={{ width: `${width}%`, background: color }} />
                                                    </div>
                                                    <em>{Number(category?.percentage || 0).toFixed(1)}%</em>
                                                </div>

                                                <div className={styles.categoryAmount}>
                                                    ₹{fmtCurrency(category?.totalExpense)}
                                                </div>
                                            </button>
                                        )
                                    })}
                                </div>
                            )}
                        </article>

                        <article className={styles.panel}>
                            <div className={styles.panelHead}>
                                <h3><Landmark size={15} /> GST & ITC Summary</h3>
                                <span>{formatMonthLabel(fiscalPeriod)}</span>
                            </div>

                            <div className={styles.statRows}>
                                <div><span>Total GST paid</span><strong>₹{fmtCurrency(dashboard.gstSummary?.totalGstPaid)}</strong></div>
                                <div><span>ITC eligible</span><strong>₹{fmtCurrency(dashboard.gstSummary?.itcEligibleAmount)}</strong></div>
                                <div><span>ITC claimed</span><strong>₹{fmtCurrency(dashboard.gstSummary?.itcClaimedAmount)}</strong></div>
                            </div>

                            {Number(dashboard.gstSummary?.itcUnclaimedAmount || 0) > 0 && (
                                <button
                                    type="button"
                                    className={styles.itcNudge}
                                    onClick={() => navigate(`/expenses?isGstItcClaimed=0&fiscalPeriod=${encodeURIComponent(fiscalPeriod)}`)}
                                >
                                    <AlertTriangle size={14} />
                                    <span>
                                        You have ₹{fmtCurrency(dashboard.gstSummary?.itcUnclaimedAmount)} unclaimed ITC.
                                        View {dashboard.gstSummary?.itcUnclaimedBillCount || 0} bill(s).
                                    </span>
                                </button>
                            )}
                        </article>

                        <article className={`${styles.panel} ${styles.recentPanel}`}>
                            <div className={styles.panelHead}>
                                <h3><Clock3 size={15} /> Recent Expenses</h3>
                                <span>Last 5 across all time</span>
                            </div>

                            {dashboard.recentExpenses.length === 0 ? (
                                <div className={styles.emptyState}><CircleSlash size={16} /> No recent expenses found.</div>
                            ) : (
                                <div className={styles.recentList}>
                                    {dashboard.recentExpenses.map((item) => (
                                        <button
                                            type="button"
                                            key={item?.expenseIdentifier}
                                            className={styles.recentItem}
                                            onClick={() => navigate(`/expenses/history/${item?.expenseIdentifier}`)}
                                        >
                                            <div className={styles.recentTop}>
                                                <span>
                                                    <span
                                                        className={styles.categoryDot}
                                                        style={{ backgroundColor: item?.topCategoryColorHex || '#B4B2A9' }}
                                                    />
                                                    {item?.topCategoryLabel || 'Uncategorized'}
                                                </span>
                                                <strong>₹{fmtCurrency(item?.totalExpense)}</strong>
                                            </div>
                                            <div className={styles.recentMeta}>
                                                <span><CheckCircle2 size={12} /> {item?.approvalStatus || '—'}</span>
                                                <span><Wallet size={12} /> {item?.paymentStatus || '—'}</span>
                                                <span><TrendingDown size={12} /> {formatExpenseDate(item?.expenseDate)}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </article>
                    </section>
                </>
            )}
        </div>
    )
}

export default ExpensesSummary